import type React from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"

interface HelpPopupProps {
  onOpenChange: (open: boolean) => void
  isOpen?: boolean
}

const HelpPopup: React.FC<HelpPopupProps> = ({ onOpenChange, isOpen = false }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[625px] bg-white">
        <DialogHeader>
          <DialogTitle>Help</DialogTitle>
          <DialogDescription>Welcome to Coverage Maps!</DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-6">
          <p className="mb-4">
            Coverage Maps is an interactive tool designed to help you manage regional coverage areas with ease. Whether
            you're assigning sales territories, service zones, or operational regions, our platform makes it simple and
            intuitive.
          </p>
          <h3 className="text-lg font-semibold mb-2">How to Use Coverage Maps</h3>
          <h4 className="font-semibold mt-3 mb-1">Start with Sample Data</h4>
          <ul className="list-disc pl-5 mb-3">
            <li>The chart includes sample data to help you get familiar with how Coverage Maps works.</li>
            <li>
              Feel free to clear the data and start fresh or modify the existing entries. The map will change as the
              table changes.
            </li>
          </ul>
          <h4 className="font-semibold mt-3 mb-1">Editing and Assigning Regions</h4>
          <ul className="list-disc pl-5 mb-3">
            <li>Click on a region (i.e. state or country) in the map to assign it to a specific coverage area.</li>
            <li>You can add new coverage areas and customize them with unique colors.</li>
          </ul>
          <h4 className="font-semibold mt-3 mb-1">Importing Your Data</h4>
          <ul className="list-disc pl-5 mb-3">
            <li>Upload a CSV file to bring in your own coverage data.</li>
            <li>The system will automatically update the map and chart based on your imported data.</li>
          </ul>
          <h4 className="font-semibold mt-3 mb-1">Exploring Features</h4>
          <ul className="list-disc pl-5 mb-3">
            <li>Toggle between US and World View depending on your coverage needs.</li>
            <li>Use the Legend Options to customize what's displayed on the map.</li>
            <li>Pan, zoom, and interact with the map to refine your territory planning.</li>
          </ul>
          <h3 className="text-lg font-semibold mt-4 mb-2">Frequently Asked Questions</h3>
          <p>
            <strong>Can I reset the data?</strong> Yes! Use the "Clear Data" option to remove sample entries and start
            fresh.
          </p>
          <p>
            <strong>What file formats can I import?</strong> CSV files with properly formatted data will integrate
            seamlessly.
          </p>
          <p>
            <strong>Will my changes be saved?</strong> Changes will persist as long as your session remains active.
          </p>
          <p className="mt-4">If you need further assistance, reach out to support@coverage-maps.com.</p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default HelpPopup
